'use strict';
(() => {
  const normalize = value => String(value || '').trim().toLocaleLowerCase();
  async function fetchCatalogPage(url, signal) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    let timedOut = false;
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', cancel, {once:true});
    const timer = setTimeout(() => {timedOut=true;controller.abort();}, 12000);
    try {
      const response = await fetch(url, {credentials:'same-origin',signal:controller.signal});
      if (!response.ok) throw new Error('Pagination failed');
      return await response.text();
    } catch (error) {
      if (timedOut) throw new Error('Pagination timed out');
      throw error;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
    }
  }
  function init(scope = document) {
    const roots = [];
    if (scope.matches?.('[data-in-artist-catalog]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-in-artist-catalog]'));
    roots.forEach(root => {
      if (root.dataset.filterReady) return;
      root.dataset.filterReady = 'true';
      const grid = root.querySelector('[data-in-catalog-grid]');
      if (!grid) return;
      const quick = root.querySelector('[data-in-quick-labels]');
      const auto = root.querySelector('[data-in-auto-labels]');
      const count = root.querySelector('[data-in-catalog-count]');
      const pagination = root.querySelector('[data-in-catalog-pagination]');
      const empty = root.querySelector('[data-in-quick-empty]');
      const status = root.querySelector('[data-in-catalog-status]');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'outline-button';
      retry.dataset.inCatalogRetry = '';
      retry.hidden = true;
      let retryText = root.dataset.errorText;
      try { retryText=JSON.parse(document.getElementById('in-cart-loading-copy')?.textContent||'{}').retry||retryText; } catch {}
      retry.textContent = retryText;
      status.insertAdjacentElement('afterend', retry);
      const stockButton = root.querySelector('[data-in-stock-toggle]');
      const labelSelect = root.querySelector('[data-in-label-select]');
      const filterDialog = root.querySelector('#InFilterDialog');
      filterDialog.addEventListener('close', () => {document.body.style.overflow='';});
      const originalCount = count.textContent;
      const originalPage = Number(grid.dataset.page) || 1;
      const items = new Map();
      const state = {label:'', stock:false, min:'', max:'', page:1};
      const pageSize = Math.max(1, Number(root.dataset.pageSize) || 10);
      const filteredPagination = document.createElement('nav');
      filteredPagination.className = 'in-pagination in-pagination--filtered';
      filteredPagination.hidden = true;
      filteredPagination.setAttribute('aria-label', root.dataset.paginationLabel || '');
      grid.insertAdjacentElement('afterend', filteredPagination);
      let next = pagination?.dataset.nextUrl || '';
      let complete = !next && originalPage === 1;
      let loading = false;
      let loadPromise;
      let failed = false;
      const abort = new AbortController();
      function collect(container, page) {
        container.querySelectorAll('[data-in-catalog-item]').forEach((card, index) => {
          const id = card.dataset.inCardId;
          if (!id || items.has(id)) return;
          if (container !== grid) { card.hidden = true; grid.append(card); }
          items.set(id, {card, order:page * 100 + index, original:page === originalPage});
        });
      }
      collect(grid, originalPage);
      const rules = () => [...quick.querySelectorAll('[data-in-quick-key]')].filter(button => button.dataset.inQuickKey);
      const ruleFor = key => rules().find(button => button.dataset.inQuickKey === key);
      const labels = card => [...card.querySelectorAll('.product-tags [data-in-badge-output] .in-product-badge')];
      function refreshLabels() {
        if (root.dataset.autoLabels === 'true') {
          const configured = new Set(rules().filter(b => b.parentElement === quick && b.dataset.inQuickKind === 'label').map(b => normalize(b.dataset.inQuickValue)));
          const seen = new Set();
          const fragment = document.createDocumentFragment();
          items.forEach(({card}) => labels(card).forEach(badge => {
            const text = badge.textContent.trim();
            const value = normalize(text);
            if (!value || configured.has(value) || seen.has(value)) return;
            seen.add(value);
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'in-quick-label';
            button.dataset.inQuickKey = 'label:' + value;
            button.dataset.inQuickKind = 'label'; button.dataset.inQuickValue = text;
            button.setAttribute('aria-pressed', 'false');
            const colors = getComputedStyle(badge);
            button.style.setProperty('--tag-bg', colors.backgroundColor || '#7024d5');
            button.style.setProperty('--tag-fg', colors.color || '#d7ff3c');
            button.textContent = text; fragment.append(button);
          }));
          auto.replaceChildren(fragment);
        }
        const options = document.createDocumentFragment();
        const all = document.createElement('option'); all.value = ''; all.textContent = 'All labels'; options.append(all);
        rules().forEach(button => {
          const option = document.createElement('option'); option.value = button.dataset.inQuickKey;
          option.textContent = button.textContent; options.append(option);
        });
        labelSelect.replaceChildren(options);
        items.forEach(({card}) => labels(card).forEach(badge => {
          const match = rules().find(b => b.dataset.inQuickKind === 'label' && normalize(b.dataset.inQuickValue) === normalize(badge.textContent));
          if (!match || badge.tagName === 'BUTTON') return;
          const button = document.createElement('button');
          button.type = 'button'; button.className = badge.className;
          button.style.cssText = badge.style.cssText; button.textContent = badge.textContent;
          button.dataset.inCardLabel = match.dataset.inQuickKey;
          button.setAttribute('aria-label', 'Filter by ' + badge.textContent.trim());
          badge.replaceWith(button);
        }));
      }
      function readState() {
        const params = new URL(location.href).searchParams;
        state.label = params.get('in_label') || '';
        state.stock = params.get('in_stock') === '1';
        state.min = params.get('in_min') || ''; state.max = params.get('in_max') || '';
        state.page = Math.max(1, parseInt(params.get('in_page'), 10) || 1);
      }
      const isActive = () => Boolean(state.label || state.stock || state.min !== '' || state.max !== '');
      function writeState() {
        const url = new URL(location.href);
        for (const [name, value] of Object.entries({in_label:state.label, in_stock:state.stock?'1':'', in_min:state.min, in_max:state.max})) {
          if (value !== '') url.searchParams.set(name, value); else url.searchParams.delete(name);
        }
        if (isActive()) url.searchParams.delete('page');
        else if (originalPage > 1) url.searchParams.set('page', String(originalPage));
        else url.searchParams.delete('page');
        if (isActive() && state.page > 1) url.searchParams.set('in_page', String(state.page));
        else url.searchParams.delete('in_page');
        history.replaceState({}, '', url);
      }
      function matches(card) {
        const rule = ruleFor(state.label);
        if (state.label && !rule) return false;
        if (rule) {
          const value = normalize(rule.dataset.inQuickValue);
          const kind = rule.dataset.inQuickKind;
          if (kind === 'label' && !labels(card).some(b => normalize(b.textContent) === value)) return false;
          if (kind === 'tag') {
            let tags = []; try { tags = JSON.parse(card.dataset.inCardTags || '[]'); } catch {}
            if (!tags.some(tag => normalize(tag) === value)) return false;
          }
          if (kind === 'type' && normalize(card.dataset.inCardType) !== value) return false;
          if (kind === 'sale' && card.dataset.inCardSale !== 'true') return false;
        }
        if (state.stock && card.dataset.inCardAvailable !== 'true') return false;
        const min = state.min === '' ? 0 : Number(state.min) * 100;
        const max = state.max === '' ? Infinity : Number(state.max) * 100;
        let prices = []; try { prices = JSON.parse(card.dataset.inCardPrices || '[]'); } catch {}
        if (!prices.length) prices = [Number(card.dataset.inCardPrice)];
        return prices.some(price => Number(price) >= min && Number(price) <= max);
      }
      function renderFilteredPagination(show, pages) {
        filteredPagination.hidden = !show;
        filteredPagination.replaceChildren();
        if (!show) return;
        const summary = document.createElement('p'); summary.className = 'in-pagination__summary';
        summary.textContent = root.dataset.pageStatusLabel.replace('__current__', String(state.page)).replace('__total__', String(pages));
        const controls = document.createElement('div'); controls.className = 'in-pagination__controls';
        const button = (page, text, label, arrow=false) => {
          const node = document.createElement('button'); node.type='button';
          node.dataset.inFilterPage=String(page); node.textContent=text; node.setAttribute('aria-label',label);
          if (arrow) node.className='in-pagination__arrow';
          return node;
        };
        if (state.page > 1) controls.append(button(state.page-1,'←',root.dataset.previousPageLabel,true));
        const selected = new Set([1,pages,state.page-1,state.page,state.page+1].filter(n=>n>=1&&n<=pages));
        let previous=0;
        [...selected].sort((a,b)=>a-b).forEach(page=>{
          if (previous && page-previous>1) {const dots=document.createElement('span');dots.className='in-pagination__ellipsis';dots.textContent='…';dots.setAttribute('aria-hidden','true');controls.append(dots);}
          const label=root.dataset.pageNumberLabel.replace('__number__',String(page));
          if (page===state.page) {const current=document.createElement('span');current.className='in-pagination__current';current.textContent=String(page);current.setAttribute('aria-current','page');current.setAttribute('aria-label',label);controls.append(current);}
          else controls.append(button(page,String(page),label));
          previous=page;
        });
        if (state.page < pages) controls.append(button(state.page+1,'→',root.dataset.nextPageLabel,true));
        filteredPagination.append(summary,controls);
      }
      let lastMotionKey = null;
      function render() {
        if (!root.isConnected) return;
        const active = isActive();
        const ordered = [...items.values()].sort((a,b) => a.order-b.order);
        const matching = active ? ordered.filter(item => matches(item.card)) : ordered.filter(item => item.original);
        const visible = matching.length;
        const pages = Math.max(1, Math.ceil(visible / pageSize));
        if (!active || complete) state.page = Math.min(state.page, pages);
        const pageItems = active ? matching.slice((state.page-1)*pageSize, state.page*pageSize) : matching;
        const shown = new Set(pageItems.map(item => item.card));
        ordered.forEach(item => { item.card.hidden = !shown.has(item.card); if (active) grid.append(item.card); });
        renderFilteredPagination(active && complete && !loading && !failed && pages > 1, pages);
        grid.querySelectorAll('.in-empty').forEach(node => { node.hidden = active; });
        if (pagination) pagination.hidden = active;
        empty.hidden = !active || visible > 0 || loading || failed;
        status.textContent = loading ? root.dataset.loadingText : failed && active ? root.dataset.errorText : '';
        retry.hidden = !failed || loading || !active;
        retry.disabled = loading;
        root.setAttribute('aria-busy', String(loading));
        count.textContent = active && !loading && !failed
          ? (visible === 1 ? root.dataset.countOne : root.dataset.countMany.replace('0', String(visible)))
          : originalCount;
        quick.querySelectorAll('[data-in-quick-key]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.inQuickKey === state.label)));
        stockButton?.setAttribute('aria-pressed', String(state.stock));
        const motionKey = pageItems.map(item => item.card.dataset.inCardId).join(',');
        document.dispatchEvent(new CustomEvent('idolnest:catalog:rendered', {detail:{grid, loading, changed:lastMotionKey !== null && lastMotionKey !== motionKey}}));
        if (!loading) lastMotionKey = motionKey;
        labelSelect.value = state.label;
        root.querySelector('[data-in-stock-input]').checked = state.stock;
        root.querySelector('[data-in-price-min]').value = state.min;
        root.querySelector('[data-in-price-max]').value = state.max;
        const sortForm = root.querySelector('.in-sort-form');
        if (sortForm) {
          for (const [name,value] of Object.entries({in_label:state.label,in_stock:state.stock?'1':'',in_min:state.min,in_max:state.max})) {
            let input = sortForm.querySelector('input[name="'+name+'"]');
            if (!input) {input=document.createElement('input');input.type='hidden';input.name=name;sortForm.append(input);}
            input.value = value;
          }
        }
      }
      async function loadAll() {
        if (complete) return;
        if (loadPromise) return loadPromise;
        loading = true; failed = false; render();
        loadPromise = (async () => {
          let href = next;
          if (originalPage > 1) { const first = new URL(location.href); first.searchParams.delete('page'); href=first.href; }
          const visited = new Set();
          while (href) {
            const url = new URL(href, location.href);
            if (url.origin !== location.origin) throw new Error('Unexpected pagination origin');
            url.searchParams.set('section_id', root.dataset.sectionId);
            if (visited.has(url.href)) throw new Error('Repeated pagination URL');
            visited.add(url.href);
            const page = new DOMParser().parseFromString(await fetchCatalogPage(url, abort.signal), 'text/html');
            const pageGrid = page.querySelector('[data-in-catalog-grid]');
            if (!pageGrid) throw new Error('Missing product grid');
            collect(pageGrid, Number(pageGrid.dataset.page) || 1);
            document.dispatchEvent(new CustomEvent('idolnest:cards:added'));
            href = page.querySelector('[data-in-catalog-pagination]')?.dataset.nextUrl || '';
          }
          complete = true;
        })().catch(error => { if (error.name !== 'AbortError') failed=true; }).finally(() => {
          loading=false;loadPromise=null;refreshLabels();render();
        });
        return loadPromise;
      }
      async function apply() { render(); if (isActive()) { await loadAll(); render(); } }
      root.addEventListener('change', event => {
        if (event.target.matches('[data-in-sort]')) {
          event.stopPropagation();event.target.form.requestSubmit();
        }
      }, true);
      root.addEventListener('click', event => {
        if (event.target.closest('[data-in-catalog-retry]')) {
          event.preventDefault();
          if (!loading) apply();
          return;
        }
        if (event.target.closest('[data-in-open-filters]')) {
          event.preventDefault();event.stopPropagation();filterDialog.showModal();document.body.style.overflow='hidden';return;
        }
        if (event.target.closest('[data-in-close-filters]')) {
          event.preventDefault();event.stopPropagation();filterDialog.close();return;
        }
        const pageButton = event.target.closest('[data-in-filter-page]');
        if (pageButton) {
          event.preventDefault(); state.page=Math.max(1, Number(pageButton.dataset.inFilterPage)||1); writeState(); render();
          grid.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'}); return;
        }
        const chip = event.target.closest('[data-in-quick-key]');
        const badge = event.target.closest('[data-in-card-label]');
        if (chip || badge) {
          event.preventDefault(); const key = chip ? chip.dataset.inQuickKey : badge.dataset.inCardLabel;
          state.label = key === state.label ? '' : key; state.page=1; writeState(); apply(); return;
        }
        if (event.target.closest('[data-in-stock-toggle]')) {state.stock=!state.stock;state.page=1;writeState();apply();}
        if (event.target.closest('[data-in-clear-quick]')) {Object.assign(state,{label:'',stock:false,min:'',max:'',page:1});writeState();apply();}
      });
      const onLabels = () => {refreshLabels();render();};
      const onHistory = () => {readState();apply();};
      document.addEventListener('idolnest:labels:updated', onLabels);
      addEventListener('popstate', onHistory);
      root.inCatalogCleanup = () => {
        abort.abort();document.removeEventListener('idolnest:labels:updated',onLabels);removeEventListener('popstate',onHistory);
      };
      readState();refreshLabels();apply();
    });
  }
  init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    if(event.target.matches?.('[data-in-artist-catalog]'))event.target.inCatalogCleanup?.();
    event.target.querySelectorAll('[data-in-artist-catalog]').forEach(root=>root.inCatalogCleanup?.());
  });
})();
